'use client';

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_BUG_PRIORITY } from '../../constants';

/**
 * Storage key for the drafts collection.
 *
 * Drafts are stored under a single key as a `{ [uuid]: BugReportDraft }` map
 * so reads/writes are O(1) and we can enforce a global cap by count without
 * scanning the localStorage namespace.
 */
const STORAGE_KEY = 'platform-admin.bug-report.drafts';

/** Maximum number of pending drafts kept simultaneously. Oldest are evicted. */
export const BUG_REPORT_DRAFTS_MAX = 10;

/** Custom event name fired on every store mutation so multiple subscribers
 *  in the same tab stay in sync without subscribing to `storage` (which only
 *  fires across tabs). */
const DRAFTS_UPDATED_EVENT = 'platform-admin.bug-report.drafts.updated';

/**
 * Persisted shape of a Bug Reporter draft.
 *
 * Note: file attachments (images, files, recorded video) are intentionally
 * excluded — `File` objects are not serializable to JSON. The user is
 * informed in the UI that attachments must be re-attached when resuming.
 */
export interface BugReportDraft {
  /** Stable id of the draft. Generated on first save, not reused. */
  uuid: string;
  /** Title field — used as the picker preview label. */
  title: string;
  description: string;
  reason: string;
  areaPath: string;
  /** ADO iteration path (System.IterationPath). Optional in storage so legacy drafts still parse. */
  iterationPath?: string;
  /** ADO priority (1-4). Optional in storage so legacy drafts still parse. */
  priority?: number;
  /** When the draft was first created (ISO 8601). */
  createdAt: string;
  /** When the draft was last touched (ISO 8601). Used to evict oldest. */
  updatedAt: string;
}

/** Fields that mutate during editing — excludes the generated metadata. */
export type BugReportDraftFields = Pick<
  BugReportDraft,
  'title' | 'description' | 'reason' | 'areaPath' | 'iterationPath' | 'priority'
>;

type DraftsMap = Record<string, BugReportDraft>;

function safeParse(raw: string | null): DraftsMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    // Strip any malformed entries so a single corrupt record does not poison the whole store.
    const out: DraftsMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (
        v &&
        typeof v === 'object' &&
        typeof (v as BugReportDraft).uuid === 'string' &&
        typeof (v as BugReportDraft).updatedAt === 'string'
      ) {
        out[k] = v as BugReportDraft;
      }
    }
    return out;
  } catch {
    return {};
  }
}

function readStore(): DraftsMap {
  if (typeof window === 'undefined') return {};
  return safeParse(window.localStorage.getItem(STORAGE_KEY));
}

function writeStore(drafts: DraftsMap): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
  window.dispatchEvent(new CustomEvent(DRAFTS_UPDATED_EVENT));
}

/**
 * Drops the oldest entries until the map is within the cap.
 * Mutates the input map.
 */
function evictOldest(drafts: DraftsMap, cap = BUG_REPORT_DRAFTS_MAX): void {
  const entries = Object.entries(drafts);
  if (entries.length <= cap) return;
  const sorted = entries
    .map(([id, draft]) => ({ id, t: draft.updatedAt }))
    .sort((a, b) => a.t.localeCompare(b.t));
  const toRemove = sorted.length - cap;
  for (let i = 0; i < toRemove; i++) {
    const entry = sorted[i];
    if (entry) {
      delete drafts[entry.id];
    }
  }
}

/**
 * Generates a draft uuid. Uses `crypto.randomUUID` when available, otherwise
 * a stable timestamp+random fallback (drafts never travel to the server, so
 * cryptographic uniqueness is not required).
 */
function generateDraftUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `draft-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * React hook exposing CRUD operations against the bug-report drafts store.
 *
 * Every mutation broadcasts a custom event; the hook also subscribes to the
 * native `storage` event for cross-tab sync.
 */
export function useBugReportDrafts() {
  const [drafts, setDrafts] = useState<BugReportDraft[]>(() =>
    Object.values(readStore()).sort((a, b) =>
      b.updatedAt.localeCompare(a.updatedAt),
    ),
  );

  const refresh = useCallback(() => {
    setDrafts(
      Object.values(readStore()).sort((a, b) =>
        b.updatedAt.localeCompare(a.updatedAt),
      ),
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onUpdate = () => refresh();
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) refresh();
    };
    window.addEventListener(DRAFTS_UPDATED_EVENT, onUpdate);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(DRAFTS_UPDATED_EVENT, onUpdate);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  /** Returns true if any field has user-provided content. */
  const isDraftEmpty = useCallback(
    (fields: BugReportDraftFields): boolean =>
      !fields.title.trim() &&
      !fields.description.trim() &&
      !fields.areaPath &&
      !fields.iterationPath &&
      // `reason` defaults to "New" — only count it as user input if changed.
      (!fields.reason || fields.reason === 'New'),
    [],
  );

  /**
   * Persists fields under the supplied draft uuid (creating a new entry if
   * needed). Returns the uuid of the upserted draft.
   *
   * Empty drafts are removed from the store so the badge does not surface
   * stub entries the user never typed into.
   */
  const upsertDraft = useCallback(
    (uuid: string | null, fields: BugReportDraftFields): string | null => {
      const store = readStore();
      const id = uuid ?? generateDraftUuid();

      if (isDraftEmpty(fields)) {
        if (store[id]) {
          delete store[id];
          writeStore(store);
        }
        return null;
      }

      const now = new Date().toISOString();
      const existing = store[id];
      store[id] = {
        uuid: id,
        title: fields.title,
        description: fields.description,
        reason: fields.reason,
        areaPath: fields.areaPath,
        iterationPath: fields.iterationPath,
        priority: fields.priority ?? DEFAULT_BUG_PRIORITY,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
      };
      evictOldest(store);
      writeStore(store);
      return id;
    },
    [isDraftEmpty],
  );

  const getDraft = useCallback((uuid: string): BugReportDraft | null => {
    const store = readStore();
    return store[uuid] ?? null;
  }, []);

  const deleteDraft = useCallback((uuid: string): void => {
    const store = readStore();
    if (!store[uuid]) return;
    delete store[uuid];
    writeStore(store);
  }, []);

  const clearAllDrafts = useCallback((): void => {
    writeStore({});
  }, []);

  return {
    /** Drafts ordered most-recent first. */
    drafts,
    /** Total count (used by the floating badge). */
    count: drafts.length,
    upsertDraft,
    getDraft,
    deleteDraft,
    clearAllDrafts,
    isDraftEmpty,
  };
}
